import { Injectable, inject } from '@angular/core';
import { ChartConstants } from '@modules/apc/apc/data/chart-modules';
import { ScalingFactor } from '@modules/report-generator/constants';
import { ReportConfig } from '@modules/report-generator/interfaces';
import { hexToRgba } from '@modules/report-generator/shared';
import { CustomRendererService } from '@shared/services/custom-renderer.service';
import * as _ from 'lodash-es';

declare let Chart: any;

const Defaults = {
  TitleColor: '#222222',
};

@Injectable({
  providedIn: 'root',
})
export class MetabolicFingerprintRadarService {
  private readonly customRendererService: CustomRendererService = inject(CustomRendererService);
  private chartInstances = new Map<string, any>();

  clearCache() {
    this.chartInstances.forEach((chart) => chart.destroy());
    this.chartInstances.clear();
  }

  destroyChart(elementId: string) {
    const chart = this.chartInstances.get(elementId);
    if (chart) {
      chart.destroy();
      this.chartInstances.delete(elementId);
    }
  }

  renderMetabolicFingerprint(metabolicPlot3: Record<string, any>, elementId?: string, config?: ReportConfig): void {
    const hasPlots = Boolean(
      metabolicPlot3.plots && Array.isArray(metabolicPlot3.plots) && metabolicPlot3.plots.length > 0
    );
    if (!hasPlots) {
      return;
    }
    const metabolicFingerprintPlot = metabolicPlot3.plots[0];
    this.generateMetabolicFingerprintRadar(
      metabolicFingerprintPlot,
      elementId || ChartConstants.METABOLIC_FINGERPRINT.ELEMENT_ID,
      config
    );
  }

  private generateMetabolicFingerprintRadar(
    metabolicFingerprintPlot: Record<string, any>,
    elementId: string,
    config?: ReportConfig
  ): void {
    const primary = config?.template?.theme?.colors?.primary;
    const chartKey = elementId;

    this.customRendererService.css(['#vo2max', '#vlamax', '#at_ana', '#fatmax', '#cardmax'], 'display', 'inline-block');
    this.customRendererService.css(
      ['#vo2max_report', '#vlamax_report', '#at_ana_report', '#fatmax_report', '#cardmax_report'],
      'display',
      'inline-block'
    );

    let data = metabolicFingerprintPlot;
    let labels: any[] = [];
    let ctx: Element | null = this.customRendererService.select(elementId + '_radar');

    if (data.texts && data.texts.length > 0) {
      labels = data.texts[1].text;
    }

    let datasets: any[] = _.map(data.radars, (obj) => {
      _.each(obj.data, (res, index) => {
        obj.data[index] = parseFloat(res).toFixed(2);
      });

      return _.extend({}, obj, {
        backgroundColor: primary ? hexToRgba(primary, 0.25) : 'rgba(255,255,255,0)',
        strokeColor: primary ?? '#000000',
        borderColor: primary ?? '#000',
        borderWidth: 1,
        pointBackgroundColor: primary ?? '#000000',
        pointBorderColor: primary ?? '#000000',
        radius: 0,
        pointRadius: 0,
      });
    });

    const fullDatasets = [
      {
        backgroundColor: 'rgba(255,255,255,0)',
        borderColor: '#898989',
        borderWidth: 1,
        strokeColor: 'rgba(0,0,0,0)',
        pointColor: 'rgba(0,0,0,0)',
        pointStrokeColor: 'rgba(0,0,0,0)',
        data: [
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
        ].splice(0, datasets[0].data.length),
        radius: 0,
        pointRadius: 0,
      },
      {
        backgroundColor: 'rgba(255,255,255,0)',
        borderColor: '#898989',
        borderWidth: 1,
        strokeColor: 'rgba(0,0,0,0)',
        pointColor: 'rgba(0,0,0,0)',
        pointStrokeColor: 'rgba(0,0,0,0)',
        data: [
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
          '0',
          '100',
        ].splice(0, datasets[0].data.length),
        radius: 0,
        pointRadius: 0,
      },
      {
        backgroundColor: 'rgba(255,255,255,1)',
        borderColor: '#898989',
        borderWidth: 1,
        strokeColor: 'rgba(0,0,0,0)',
        pointColor: 'rgba(0,0,0,0)',
        pointStrokeColor: 'rgba(0,0,0,0)',
        data: [
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
          '25',
        ].splice(0, datasets[0].data.length),
        radius: 0,
        pointRadius: 0,
      },
      {
        backgroundColor: 'rgba(255,255,255,1)',
        borderColor: '#898989',
        borderWidth: 1,
        strokeColor: 'rgba(0,0,0,0)',
        pointColor: 'rgba(0,0,0,0)',
        pointStrokeColor: 'rgba(0,0,0,0)',
        data: [
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
          '50',
        ].splice(0, datasets[0].data.length),
        radius: 0,
        pointRadius: 0,
      },
      {
        backgroundColor: 'rgba(255,255,255,1)',
        borderColor: '#898989',
        borderWidth: 1,
        strokeColor: 'rgba(0,0,0,0)',
        pointColor: 'rgba(0,0,0,0)',
        pointStrokeColor: 'rgba(0,0,0,0)',
        data: [
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
          '75',
        ].splice(0, datasets[0].data.length),
        radius: 0,
        pointRadius: 0,
      },
      {
        backgroundColor: 'rgba(255,255,255,1)',
        borderColor: '#898989',
        borderWidth: 1,
        strokeColor: 'rgba(0,0,0,0)',
        pointColor: 'rgba(0,0,0,0)',
        pointStrokeColor: 'rgba(0,0,0,0)',
        data: [
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
          '100',
        ].splice(0, datasets[0].data.length),
        radius: 0,
        pointRadius: 0,
      },
    ];
    fullDatasets.unshift(datasets[0]);

    //custom the label
    const data_radar = {
      labels: labels,
      datasets: fullDatasets,
    };

    // Fill colors
    try {
      if (data_radar.datasets[0].data.length > 0) {
        // data_radar.datasets[0].fill_color = '#228bdf';
      }
    } catch (error) {
      console.log('RadarChart error cannot fill colors.');
    }

    const configObj = {
      type: 'radar',
      data: data_radar,
      options: {
        scale: {
          afterFit: (scale: any): void => {
            let ticks: string[] = ['0', '25', '50', '75', '100'];
            let ticksAsNumbers: number[] = [0, 25, 50, 75, 100];
            scale.ticks = ticks;
            scale.ticksAsNumbers = ticksAsNumbers;
          },
          reverse: false,
          ticks: {
            beginAtZero: true,
            fixedStepSize: 20,
            max: 100,
            showLabelBackdrop: false,
            display: false,
          },
          pointLabels: {
            fontSize: 12,
            fontColor: primary ?? Defaults.TitleColor,
            fontStyle: 'bold',
            fontFamily: '"Helvetica Neue", "Open Sans", sans-serif',
          },
          gridLines: {
            color: '#999',
            zeroLineColor: '#999',
          },
        },
        responsive: true,
        maintainAspectRatio: true,
        legend: {
          display: false,
        },
        animation: {
          duration: 0,
          onComplete: () => {
            // funtion
          },
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem: any, chart: any) => {
              return chart.datasets[0].data_tooltips[tooltipItem.index].toFixed(2);
            },
          },
        },
      },
    };

    const existingChart = this.chartInstances.get(chartKey);
    if (existingChart) {
      existingChart.data = data_radar;
      existingChart.options = configObj.options;
      existingChart.update('none');
    } else {
      const newChart = new Chart(ctx, configObj);
      this.chartInstances.set(chartKey, newChart);
    }
  }
}
